import {ProductReview} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_reviews.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  FONT_SIZES,
  HorizontalSlider,
  StarRating,
  Text,
} from '@aboutyou/ui-components';
import React, {FC, useState} from 'react';
import styled, {css} from 'styled-components';
import {starRatingMap} from './starRatingMap';

export const ReviewsUserReview: FC<{
  showcaseReviews: ProductReview[];
}> = ({showcaseReviews}) => {
  const UsersReviewsComponent =
    showcaseReviews.length > 1 ? HorizontalSlider : UsersReviewsContainer;

  const usersReviewsProps =
    showcaseReviews.length > 1
      ? {
          slideCount: 1,
          disableHeaderControl: true,
          hideSideButtons: true,
          dataTestId: 'userReviewsSlider',
        }
      : {
          'data-testid': 'userReviewsStatic',
        };

  return (
    <UsersReviewsComponent {...usersReviewsProps}>
      {showcaseReviews.map((caseReview, index) => (
        <UserReviewCard
          key={`showcase-reviews-${index}`}
          showSlidesSpacer={showcaseReviews.length > 1}
        >
          <UserReviewInnerCard data-testid={`userReview-${index}`}>
            <UserReviewStarsAndUser>
              <StarRating
                value={starRatingMap[caseReview.stars]}
                size="L"
                dataTestid={`userReviewStarRating-${index}`}
              />
              <Text
                color="Grey1"
                data-testid={`userReviewAuthorLabel-${index}`}
              >
                {caseReview.authorLabel}
              </Text>
            </UserReviewStarsAndUser>
            <ReviewTitle weight="bold" data-testid={`userReviewTitle-${index}`}>
              {caseReview.title}
            </ReviewTitle>
            <UserTextReviews
              reviewText={caseReview.text}
              dataTestId={`userReviewText-${index}`}
            />
          </UserReviewInnerCard>
        </UserReviewCard>
      ))}
    </UsersReviewsComponent>
  );
};

const UserTextReviews: FC<{reviewText: string; dataTestId: string}> = ({
  reviewText,
  dataTestId,
}) => {
  const [shortenText, setShortenText] = useState(true);
  const {t} = useTranslation();
  const MAX_REVIEW_TEXT_CHARACTERS = 120;
  const isLongText = reviewText.length > MAX_REVIEW_TEXT_CHARACTERS;

  return (
    <ReviewText data-testid={dataTestId}>
      {isLongText && shortenText
        ? `${reviewText.substring(0, MAX_REVIEW_TEXT_CHARACTERS)}...`
        : reviewText}
      {isLongText && (
        <SeeMoreLessButton
          showInNextLine={!shortenText}
          onClick={() => setShortenText(!shortenText)}
        >
          {' '}
          {shortenText ? t('Read more') : t('Read less')}
        </SeeMoreLessButton>
      )}
    </ReviewText>
  );
};

const UserReviewCard = styled.div<{showSlidesSpacer: boolean}>`
  border: 1px solid ${COLORS.Grey3};
  border-radius: 2px;
  box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.02), 0 0 15px 0 rgba(0, 0, 0, 0.04);
  ${({showSlidesSpacer}) =>
    showSlidesSpacer &&
    css`
      margin: 0 5px;
    `}
`;

const UserReviewInnerCard = styled.div`
  padding: 16px 16px 42px 16px;
`;

const UserReviewStarsAndUser = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: space-between;
`;

const ReviewTitle = styled(Text)`
  display: block;
  margin-bottom: 8px;
  margin-top: 16px;
  text-transform: capitalize;
`;

const ReviewText = styled(Text)`
  display: block;
  line-height: ${FONT_SIZES.px20};
`;

const UsersReviewsContainer = styled.div``;

const SeeMoreLessButton = styled.span<{showInNextLine: boolean}>`
  color: ${COLORS.Grey1};
  ${({showInNextLine}) =>
    showInNextLine &&
    css`
      display: block;
    `}
`;
